import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Chip, MenuItem, Alert, CircularProgress, Grid
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import ModuleHub from '../components/ModuleHub';
import {
  getChartOfAccounts, createChartOfAccount, updateChartOfAccount, deleteChartOfAccount
} from '../../../services/api';

const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'income', 'expense'];

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    account_code: '', account_name: '', account_type: 'asset', description: '', is_header: false
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getChartOfAccounts();
      setAccounts(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditItem(null);
    setForm({ account_code: '', account_name: '', account_type: 'asset', description: '', is_header: false });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      account_code: item.account_code,
      account_name: item.account_name,
      account_type: item.account_type,
      description: item.description || '',
      is_header: item.is_header || false
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editItem) await updateChartOfAccount(editItem.id, form);
      else await createChartOfAccount(form);
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try {
      await deleteChartOfAccount(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const typeColor = (type) => ({
    asset: 'primary', liability: 'error', equity: 'secondary', income: 'success', expense: 'warning'
  }[type] || 'default');

  return (
    <ModuleHub moduleId="general-ledger" showFeatures={false}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Account</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((acc) => (
                <TableRow key={acc.id}>
                  <TableCell><strong>{acc.account_code}</strong></TableCell>
                  <TableCell>{acc.account_name}</TableCell>
                  <TableCell><Chip label={acc.account_type} size="small" color={typeColor(acc.account_type)} /></TableCell>
                  <TableCell align="right">{parseFloat(acc.balance || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' })}</TableCell>
                  <TableCell><Chip label={acc.is_active ? 'Active' : 'Inactive'} size="small" color={acc.is_active ? 'success' : 'default'} /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(acc)}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(acc.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Account' : 'New Account'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}><TextField fullWidth label="Account Code" value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <TextField fullWidth select label="Type" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
                {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Account Name" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </ModuleHub>
  );
};

export default ChartOfAccounts;
